import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, ContactNoteDto, CreateContactNoteBody } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { contactNotesQueryKey } from '@/hooks/queries/use-contact-notes-query';

interface UseCreateContactNoteOptions {
  /** Resolved contact id the note is attached to; also the query key. */
  creatorIdentityId: string | null;
  /** Author name stamped on the optimistic row (the server returns the truth). */
  optimisticAuthorName: string;
}

interface CreateContactNoteContext {
  previous?: ContactNoteDto[];
}

const createContactNote = async (
  creatorIdentityId: string,
  body: CreateContactNoteBody
): Promise<ContactNoteDto> => {
  const { data } = await api.post<ApiResponse<ContactNoteDto>>(
    `/dashboard/contacts/${creatorIdentityId}/notes`,
    body
  );

  if (!data.data) {
    throw new Error('Contact note not created');
  }

  return data.data;
};

/**
 * Creates a team note on a contact, optimistically prepending it to the cached
 * notes list. Mirrors the wishlist action hooks for the error-toast + envelope
 * conventions; adds optimistic cache work (cancel + snapshot + setQueryData,
 * rollback on error, invalidate on settled) so the new note shows instantly and
 * is reconciled with the server row once the request settles.
 */
export const useCreateContactNote = ({
  creatorIdentityId,
  optimisticAuthorName,
}: UseCreateContactNoteOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = contactNotesQueryKey(creatorIdentityId);

  return useMutation<ContactNoteDto, AxiosError<ApiResponse>, string, CreateContactNoteContext>({
    mutationFn: (body: string) => createContactNote(creatorIdentityId!, { body }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ContactNoteDto[]>(queryKey);

      const optimistic: ContactNoteDto = {
        id: `optimistic-${Date.now()}`,
        authorName: optimisticAuthorName,
        body,
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueryData<ContactNoteDto[]>(queryKey, (current) => [
        optimistic,
        ...(current ?? []),
      ]);

      return { previous };
    },
    onError: (error, _body, context) => {
      queryClient.setQueryData(queryKey, context?.previous);

      const errorMessage = error.response?.data?.message || t('common.errors.unexpected');

      toaster.error({
        title: t('contactDrawer.notes.addError'),
        description: errorMessage,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
